import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchCartItems, 
  selectCartState, 
  selectIsUserLoggedIn, 
  selectIsAdminLoggedIn,
  removeFromCart,
  addToCart,
  setCartItems,
  selectUser,
  selectWithcookieData
} from '../../../redux/WithcookieSlice';
import { useTranslation } from '../../../redux/TranslationHelper';
import { PriceDisplay } from '../../common/PriceDisplay';
import SafeImage from '../../common/SafeImage';
import { CartProps, CartItem } from './Cart.types';
import { adaptCartProps } from './Cart.adapter';
import './Cart.css';

const CartComponent: React.FC<CartProps> = (originalProps) => {
  // 어댑터를 통해 속성 변환
  const props = adaptCartProps(originalProps);
  const { t } = useTranslation();
  
  // 컴포넌트 속성 추출
  const { 
    className = "cart-component-wrapper",
    title = t("장바구니"),
    emptyText = t("장바구니가 비어 있습니다."),
    shippingFee = 4000,
    showStock = false
  } = props.componentProps || props;
  
  const dispatch = useDispatch();
  const { cartItems, loading, error } = useSelector(selectCartState);
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const isAdminLoggedIn = useSelector(selectIsAdminLoggedIn);
  const withcookieData = useSelector((state: any) => state.withcookie?.withcookieData);
  const userInfo = useSelector(selectUser);
  const [removeLoading, setRemoveLoading] = useState<boolean>(false);
  
  // 로컬에서 수량을 관리하기 위한 상태
  const [localQuantities, setLocalQuantities] = useState<Record<number, number>>({});
  
  // debounced API 호출을 위한 ref
  const debouncedUpdateRefs = useRef<Record<number, NodeJS.Timeout>>({});
  
  // 간단한 debounce 함수
  const debounce = useCallback((func: Function, delay: number) => {
    return (...args: any[]) => {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(func(...args));
        }, delay);
      });
    };
  }, []);
  
  // 회사 설정에서 무료배송 기준금액 가져오기
  const freeShippingLimit = useMemo(() => {
    try {
      // withcookieData에서 companySettingJson 값 찾기
      let companySettingJson = null;
      
      // 서버에서 받은 데이터 구조에 따라 여러 경로 확인
      if (withcookieData?.company?.companySettingJson) {
        companySettingJson = withcookieData.company.companySettingJson;
      } else if (withcookieData?.skin?.company?.companySettingJson) {
        companySettingJson = withcookieData.skin.company.companySettingJson;
      }
      
      // JSON 문자열 또는 객체 처리
      if (typeof companySettingJson === 'string') {
        const parsedSettings = JSON.parse(companySettingJson);
        return parsedSettings.free_shipping_limit || 50000; // 기본값 50000
      } else if (companySettingJson && typeof companySettingJson === 'object') {
        return companySettingJson.free_shipping_limit || 50000; // 기본값 50000
      }
      
      return 50000; // 기본값
    } catch (error) {
      console.error('무료배송 기준금액 가져오기 오류:', error);
      return 50000; // 에러 발생 시 기본값
    }
  }, [withcookieData]);
  
  const navigate = useNavigate();
  
  // 직급별 가격 계산 함수
  const calculateLevelPrice = useCallback((basePrice: number, product: any) => {
    // 사용자 정보가 없거나 로그인하지 않은 경우
    if (!userInfo || !isUserLoggedIn) {
      return {
        originalPrice: basePrice,
        levelPrice: basePrice,
        discount: 0,
        discountRate: 0,
        levelName: null
      };
    }
    
    // 사용자의 level2 ID 확인
    const userLevel2Id = userInfo.level2;
    const userLevel2Policy = userInfo.level2Policy;
    
    // level2가 없으면 할인 없음
    if (!userLevel2Id) {
      return {
        originalPrice: basePrice,
        levelPrice: basePrice,
        discount: 0,
        discountRate: 0,
        levelName: null
      };
    }
    
    // 상품의 optionJson에서 level2_price 확인
    let levelPrice = basePrice;
    let levelName = '직급 할인'; // 기본 직급명
    
    // level2Policy가 있으면 직급명 사용
    if (userLevel2Policy) {
      levelName = userLevel2Policy.customName || userLevel2Policy.type || '직급 할인';
    } else {
      // withcookieData에서 직급명 찾기
      const levelPolicies = withcookieData?.skin?.company?.levelpolicies || withcookieData?.company?.levelpolicies || [];
      const matchingPolicy = levelPolicies.find((policy: any) => policy.id === userLevel2Id);
      if (matchingPolicy) {
        levelName = matchingPolicy.customName || matchingPolicy.type || '직급 할인';
      }
    }
    
    if (product?.optionJson?.level2_price && product.optionJson.level2_price[userLevel2Id]) {
      // level2_price에 해당 레벨의 가격이 있으면 그 가격 사용
      levelPrice = Number(product.optionJson.level2_price[userLevel2Id]) || basePrice;
    } else if (userLevel2Policy?.settingJson?.supply_price_ratio) {
      // level2_price가 없으면 supply_price_ratio 사용
      const supplyPriceRatio = Number(userLevel2Policy.settingJson.supply_price_ratio) || 1;
      levelPrice = Math.floor(basePrice * supplyPriceRatio);
    } else {
      // withcookieData에서 supply_price_ratio 찾기
      const levelPolicies = withcookieData?.skin?.company?.levelpolicies || withcookieData?.company?.levelpolicies || [];
      const matchingPolicy = levelPolicies.find((policy: any) => policy.id === userLevel2Id);
      if (matchingPolicy?.settingJson?.supply_price_ratio) {
        const supplyPriceRatio = Number(matchingPolicy.settingJson.supply_price_ratio) || 1;
        levelPrice = Math.floor(basePrice * supplyPriceRatio);
      }
    }
    
    const discount = basePrice - levelPrice;
    const discountRate = basePrice > 0 ? Math.round((discount / basePrice) * 100) : 0;
    
    return {
      originalPrice: basePrice,
      levelPrice,
      discount,
      discountRate,
      levelName
    };
  }, [userInfo, isUserLoggedIn]);

  // 컴포넌트 마운트 시 장바구니 데이터 로드
  useEffect(() => {
    if (isUserLoggedIn || isAdminLoggedIn) {
      // @ts-ignore - Redux Toolkit 타입 오류 무시
      dispatch(fetchCartItems());
    }
  }, [dispatch, isUserLoggedIn, isAdminLoggedIn]);
  
  // 컴포넌트 언마운트 시 debounced 함수들 정리
  useEffect(() => {
    return () => {
      Object.values(debouncedUpdateRefs.current).forEach(timeoutId => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      });
    };
  }, []);

  // 초기 수량 설정
  useEffect(() => {
    if (cartItems && Array.isArray(cartItems)) {
      const initialQuantities: Record<number, number> = {};
      cartItems.forEach((item: CartItem) => {
        initialQuantities[item.id] = item.count || 1;
      });
      setLocalQuantities(initialQuantities);
    }
  }, [cartItems]);

  // 장바구니 상품 삭제 처리
  const handleRemoveFromCart = useCallback(async (cartItemId: number) => {
    if (!cartItemId) {
      alert(t('삭제할 상품 정보가 올바르지 않습니다.'));
      return;
    }
    
    try {
      setRemoveLoading(true);
      // 장바구니에서 상품 삭제
      // @ts-ignore - Redux Toolkit 타입 오류 무시
      await dispatch(removeFromCart(cartItemId)).unwrap();
      // 장바구니 목록 다시 불러오기
      // @ts-ignore - Redux Toolkit 타입 오류 무시
      await dispatch(fetchCartItems());
      alert(t('상품이 장바구니에서 삭제되었습니다.'));
    } catch (error: any) {
      console.error('장바구니 상품 삭제 중 오류:', error);
      
      // 오류가 인증 관련 문제인 경우
      if (error.code === 'AUTH_REQUIRED') {
        alert(t('로그인이 필요한 서비스입니다. 로그인 페이지로 이동합니다.'));
        window.location.href = '/login';
      } else {
        alert(t('장바구니 상품 삭제에 실패했습니다. 다시 시도해주세요.'));
      }
    } finally {
      setRemoveLoading(false);
    }
  }, [dispatch, t]);

  // 장바구니 비우기
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleClearCart = useCallback(async () => {
    // 확인 다이얼로그
    if (!window.confirm(t('장바구니를 비우시겠습니까?'))) {
      return;
    }
    
    try {
      // 장바구니의 모든 상품에 대해 삭제 요청
      if (cartItems && cartItems.length > 0) {
        setRemoveLoading(true);
        
        // 개별 삭제가 아닌 일괄 삭제로 수정
        for (const item of cartItems) {
          // @ts-ignore - Redux Toolkit 타입 오류 무시
          await dispatch(removeFromCart(item.id)).unwrap();
        }
        
        // 삭제 완료 후 장바구니 목록 다시 불러오기 (한 번만)
        // @ts-ignore - Redux Toolkit 타입 오류 무시
        await dispatch(fetchCartItems());
        alert(t('장바구니가 비워졌습니다.'));
      }
    } catch (error) {
      console.error('장바구니 비우기 중 오류:', error);
      alert(t('장바구니 비우기에 실패했습니다. 다시 시도해주세요.'));
    } finally {
      setRemoveLoading(false);
    }
  }, [cartItems, dispatch, t]);

  // API 호출을 위한 debounced 함수 생성
  const updateCartQuantity = useCallback((item: CartItem, newQuantity: number) => {
    // @ts-ignore - Redux Toolkit 타입 오류 무시
    dispatch(addToCart({ 
      product_id: item.productId || item.product.id,
      variant_id: item.variantId || undefined,
      count: newQuantity
    })).catch((error: any) => {
      console.error('수량 변경 중 오류:', error);
      
      // 실패 시 원래 수량으로 롤백
      const originalCount = item.count || 1;
      setLocalQuantities(prev => ({
        ...prev,
        [item.id]: originalCount
      }));
      
      // Redux 스토어도 롤백
      const rollbackItems = cartItems?.map(cartItem => {
        if (cartItem.id === item.id) {
          return { ...cartItem, count: originalCount };
        }
        return cartItem;
      });
      if (rollbackItems) {
        dispatch(setCartItems(rollbackItems));
      }
      
      // 에러 알림
      if (error.code === 'AUTH_REQUIRED') {
        alert(t('로그인이 필요한 서비스입니다.'));
      } else {
        const errorMessage = error.message || t('수량 변경에 실패했습니다.');
        alert(t('수량 변경 실패') + ': ' + errorMessage);
      }
    });
  }, [dispatch, cartItems, t]);

  // 수량 증가 처리
  const handleIncreaseQuantity = useCallback((item: CartItem) => {
    const currentCount = item.count || 1;
    const newQuantity = currentCount + 1;
    
    // 로컬 상태 즉시 업데이트
    setLocalQuantities(prev => ({
      ...prev,
      [item.id]: newQuantity
    }));
    
    // Redux 스토어 즉시 업데이트
    if (cartItems && Array.isArray(cartItems)) {
      const updatedCartItems = cartItems.map(cartItem => {
        if (cartItem.id === item.id) {
          return { ...cartItem, count: newQuantity };
        }
        return cartItem;
      });
      dispatch(setCartItems(updatedCartItems));
    }
    
    // 기존 timeout이 있으면 취소
    if (debouncedUpdateRefs.current[item.id]) {
      clearTimeout(debouncedUpdateRefs.current[item.id]);
    }
    
    // 새로운 timeout 설정
    debouncedUpdateRefs.current[item.id] = setTimeout(() => {
      updateCartQuantity(item, newQuantity);
    }, 300);
  }, [cartItems, dispatch, updateCartQuantity]);
  
  // 수량 감소 처리
  const handleDecreaseQuantity = useCallback((item: CartItem) => {
    const currentCount = item.count || 1;
    
    // 수량이 1보다 클 때만 감소
    if (currentCount <= 1) return;
    
    const newQuantity = currentCount - 1;
    
    // 로컬 상태 즉시 업데이트
    setLocalQuantities(prev => ({
      ...prev,
      [item.id]: newQuantity
    }));
    
    // Redux 스토어 즉시 업데이트
    if (cartItems && Array.isArray(cartItems)) {
      const updatedCartItems = cartItems.map(cartItem => {
        if (cartItem.id === item.id) {
          return { ...cartItem, count: newQuantity };
        }
        return cartItem;
      });
      dispatch(setCartItems(updatedCartItems));
    }
    
    // 기존 timeout이 있으면 취소
    if (debouncedUpdateRefs.current[item.id]) {
      clearTimeout(debouncedUpdateRefs.current[item.id]);
    }
    
    // 새로운 timeout 설정
    debouncedUpdateRefs.current[item.id] = setTimeout(() => {
      updateCartQuantity(item, newQuantity);
    }, 300);
  }, [cartItems, dispatch, updateCartQuantity]);

  // 상품 가격 합계 계산
  const calculateTotalPrice = useCallback(() => {
    if (!cartItems || !Array.isArray(cartItems) || cartItems.length === 0) {
      return 0;
    }
    
    return cartItems.reduce((sum, item) => {
      const basePrice = item.product?.config?.discounted_price 
        ? Number(item.product.config.discounted_price) 
        : item.product?.config?.default_price 
          ? Number(item.product.config.default_price) 
          : 0;
      
      // 직급별 가격 계산
      const priceInfo = calculateLevelPrice(basePrice, item.product);
      const finalBasePrice = priceInfo.levelPrice;
      
      // Variant 추가 가격 포함
      const additionalPrice = item.variant?.additionalPrice 
        ? Number(item.variant.additionalPrice) 
        : 0;
      
      const totalPrice = finalBasePrice + additionalPrice;
      
      // 로컬 수량을 사용하여 합계 계산
      const quantity = localQuantities[item.id] || item.count || 1;
      return sum + (totalPrice * quantity);
    }, 0);
  }, [cartItems, localQuantities, calculateLevelPrice]);

  // 총 금액 계산 (상품 가격 + 배송비)
  const calculateGrandTotal = useCallback(() => {
    const totalPrice = calculateTotalPrice();
    
    // 주문 금액이 무료배송 기준금액보다 크거나 같으면 배송비 무료(0원)
    const appliedShippingFee = totalPrice >= freeShippingLimit ? 0 : shippingFee;
    
    return totalPrice + appliedShippingFee;
  }, [calculateTotalPrice, shippingFee, freeShippingLimit]);

  // 결제 페이지로 이동하는 함수
  const handleCheckout = useCallback(() => {
    // 장바구니가 비어있는지 확인
    if (!cartItems || cartItems.length === 0) {
      alert(t('장바구니가 비어 있습니다. 상품을 추가해주세요.'));
      return;
    }
    
    // 결제 페이지로 이동
    navigate('/checkout');
  }, [cartItems, navigate, t]);

  // 로딩 상태 표시
  if (loading || removeLoading) {
    return (
      <div className={className} style={props.style}>
        <div className="container">
          <div className="row cart-row">
            <div className="col-12">
              <div className="cart-loading">
                <p>{t('장바구니 정보를 불러오는 중입니다...')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 에러 표시
  if (error) {
    return (
      <div className={className} style={props.style}>
        <div className="container">
          <div className="row cart-row">
            <div className="col-12">
              <div className="cart-error">
                <p>{t('장바구니 정보를 불러오는데 실패했습니다')}: {typeof error === 'object' ? (error as any).message || JSON.stringify(error) : error}</p>
                <button 
                  className="retry-button"
                  onClick={() => {
                    // @ts-ignore - Redux Toolkit 타입 오류 무시
                    dispatch(fetchCartItems());
                  }}
                >
                  {t('다시 시도')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 로그인 필요
  if (!isUserLoggedIn && !isAdminLoggedIn) {
    return (
      <div className={className} style={props.style}>
        <div className="container">
          <div className="row cart-row">
            <div className="col-12">
              <div className="cart-login-required">
                <p>{t('장바구니를 확인하려면 로그인이 필요합니다.')}</p>
                <Link to="/login" className="login-button">{t('로그인')}</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 장바구니가 비어있는 경우
  if (!cartItems || (Array.isArray(cartItems) && cartItems.length === 0)) {
    return (
      <div className={className} style={props.style}>
        <div className="container">
          <div className="row cart-row">
            <div className="col-12">
              <div className="cart-empty">
                <h2>{title}</h2>
                <p>{emptyText}</p>
                <Link to="/shopping" className="continue-shopping-button">
                  {t('쇼핑 계속하기')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 장바구니에 상품이 있는 경우 - 새로운 디자인
  return (
    <div className={className} style={props.style}>
      <div className="container">
        <div className="row cart-row">
          <div className="col-12">
            <h2 className="cart-page-title">{title}</h2>
          </div>
        </div>
        <div className="row cart-row">
          {/* 장바구니 상품 목록 */}
          <div className="col-lg-8 col-md-12">
            <div className="cart-items-list">
              {cartItems.map((item) => {
                // 상품 정보와 가격 가져오기
                const product = item.product || {};
                const config = product.config || {};
                const discountedPrice = config.discounted_price ? Number(config.discounted_price) : 0;
                const defaultPrice = config.default_price ? Number(config.default_price) : 0;
                const basePrice = discountedPrice || defaultPrice;
                
                // 직급별 가격 계산
                const priceInfo = calculateLevelPrice(basePrice, product);
                const finalBasePrice = priceInfo.levelPrice;
                
                // Variant 추가 가격
                const additionalPrice = item.variant?.additionalPrice 
                  ? Number(item.variant.additionalPrice) 
                  : 0;
                
                // 최종 가격 (직급별 가격 + 추가 가격)
                const price = finalBasePrice + additionalPrice;
                
                // 수량은 로컬 상태에서 가져옴
                const count = localQuantities[item.id] || item.count || 1;
                
                return (
                  <div className="cart-item" key={item.id}>
                    <div className="cart-item-inner">
                      <div className="cart-item-top">
                        <div className="cart-item-image">
                          <Link to={`/products/${product.id}`}>
                            {/* @ts-ignore - SafeImage 컴포넌트 타입 오류 무시 */}
                            <SafeImage
                              src={config.img_url}
                              alt={product.title}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                              placeholderStyle={{ width: '100%', height: '100%', background: '#cccccc', borderRadius: '4px' }}
                            />
                          </Link>
                        </div>
                        <div className="cart-item-details">
                          <div className="cart-item-title">
                            <Link to={`/products/${product.id}`}>
                              {t(product.title) || t('상품명 없음')}
                            </Link>
                          </div>
                          
                          {/* 옵션 정보 표시 */}
                          {item.variant && item.variant.options && item.variant.options.length > 0 && (
                            <div className="cart-item-options">
                              {item.variant.options.map((opt: any, index: number) => (
                                <span key={index} className="option-item">
                                  {opt.optionValue.optionGroup.name}: {opt.optionValue.value}
                                  {index < item.variant.options.length - 1 && ', '}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          {/* 레거시 옵션 정보 표시 (options 객체가 있는 경우) */}
                          {!item.variant && item.options && Object.keys(item.options).length > 0 && (
                            <div className="cart-item-options">
                              {(() => {
                                // sku, price, pv 등을 제외한 실제 옵션만 표시
                                const optionEntries = Object.entries(item.options)
                                  .filter(([key]) => !['sku', 'price', 'pv'].includes(key));
                                
                                if (optionEntries.length === 0) return null;
                                
                                return optionEntries.map(([key, value], index) => (
                                  <span key={key} className="option-item">
                                    {`${key}: ${value}${index < optionEntries.length - 1 ? ', ' : ''}`}
                                  </span>
                                ));
                              })()}
                            </div>
                          )}
                          
                          <div className="cart-item-price">
                            {/* 직급별 가격이 있는 경우 */}
                            {priceInfo.discount > 0 && priceInfo.levelName ? (
                              <>
                                <div className="level-price-info">
                                  <span className="level-badge-small">{priceInfo.levelName}</span>
                                  <span className="original-price-small">
                                    {/* @ts-ignore - PriceDisplay 컴포넌트 타입 오류 무시 */}
                                    <PriceDisplay price={basePrice} />
                                  </span>
                                  <span className="arrow">→</span>
                                  <span className="level-price-small">
                                    {/* @ts-ignore - PriceDisplay 컴포넌트 타입 오류 무시 */}
                                    <PriceDisplay price={finalBasePrice} />
                                  </span>
                                </div>
                              </>
                            ) : (
                              <>
                                {/* @ts-ignore - PriceDisplay 컴포넌트 타입 오류 무시 */}
                                <PriceDisplay price={finalBasePrice} />
                              </>
                            )}
                            
                            {/* Variant 추가 가격 표시 */}
                            {item.variant && Number(item.variant.additionalPrice) > 0 && (
                              <span className="variant-additional-price">
                                (+₩{Number(item.variant.additionalPrice).toLocaleString()})
                              </span>
                            )}
                          </div>
                          {showStock && config.stock_count !== undefined && (
                            <div className="cart-item-stock">
                              {t('재고')}: {config.stock_count}{t('개')}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="cart-item-actions">
                        <div className="cart-item-quantity">
                          <div className="cart-quantity-control">
                            <button 
                              className="quantity-decrease" 
                              onClick={() => handleDecreaseQuantity(item)}
                              disabled={count <= 1}
                            >-</button>
                            <span className="quantity-display">{localQuantities[item.id] || count}</span>
                            <button 
                              className="quantity-increase" 
                              onClick={() => handleIncreaseQuantity(item)}
                            >+</button>
                          </div>
                        </div>
                        <div className="cart-item-total">
                          {/* @ts-ignore - PriceDisplay 컴포넌트 타입 오류 무시 */}
                          <PriceDisplay price={price * count} />
                        </div>
                        <div className="cart-item-remove">
                          <button 
                            onClick={() => handleRemoveFromCart(item.id)}
                            disabled={removeLoading}
                            aria-label={t('상품 삭제')}
                          >
                            <i className="fa fa-times" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* 장바구니 요약 및 결제 정보 */}
          <div className="col-lg-4 col-md-12">
            <div className="cart-summary">
              <div className="cart-summary-inner">
                <div className="summary-title">
                  <h3>{t('장바구니 금액')}</h3>
                </div>
                <div className="summary-item">
                  <div className="item-label">{t('상품 금액')}</div>
                  <div className="item-value">
                    {/* @ts-ignore - PriceDisplay 컴포넌트 타입 오류 무시 */}
                    <PriceDisplay price={calculateTotalPrice()} />
                  </div>
                </div>
                <div className="summary-item">
                  <div className="item-label">{t('배송비')}</div>
                  <div className="item-value">
                    {/* 무료배송 여부에 따라 배송비 표시 */}
                    {calculateTotalPrice() >= freeShippingLimit ? (
                      <span className="free-shipping">{t('무료배송')}</span>
                    ) : (
                      /* @ts-ignore - PriceDisplay는 price prop을 받는 컴포넌트입니다 */
                      <PriceDisplay price={shippingFee} />
                    )}
                  </div>
                </div>
                <div className="summary-total">
                  <div className="total-label">{t('총 결제금액')}</div>
                  <div className="total-value">
                    {/* @ts-ignore - PriceDisplay 컴포넌트 타입 오류 무시 */}
                    <PriceDisplay price={calculateGrandTotal()} />
                  </div>
                </div>
                <div className="summary-action">
                  <button 
                    className="checkout-button"
                    onClick={handleCheckout}
                  >
                    {t('결제하기')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartComponent; 